import type { TrackingEvent } from '~/utils/tracking'

/**
 * Live call-tracking events (/ws/tracking/): an incoming call (caller card) or a finished sync.
 * Reconnects with back-off while the component that uses it is mounted.
 */
export function useTrackingEvents(handler: (event: TrackingEvent) => void) {
  const config = useRuntimeConfig()
  let socket: WebSocket | null = null
  let retry = 0
  let timer: ReturnType<typeof setTimeout> | undefined
  let closed = false

  const connect = () => {
    const base = config.public.baseURL || window.location.origin + '/api'
    const url = `${base}/ws/tracking/`
      .replace(/^http:\/\//, 'ws://')
      .replace(/^https:\/\//, 'wss://')
    socket = new WebSocket(url)
    socket.onopen = () => (retry = 0)
    socket.onmessage = ({ data }) => {
      try {
        handler(JSON.parse(data) as TrackingEvent)
      } catch {
        // ignore malformed events
      }
    }
    socket.onclose = (event) => {
      // 4403 = no access to call tracking: do not retry
      if (closed || event.code === 4403) return
      retry = Math.min(retry + 1, 6)
      timer = setTimeout(connect, 1000 * 2 ** retry)
    }
  }

  onMounted(connect)
  onBeforeUnmount(() => {
    closed = true
    clearTimeout(timer)
    socket?.close()
  })
}
